import { type BookingsSortField } from "@/bookings/BookingsTable/constants";
import { type PageSize } from "@/components/Pagination/usePagination";
import { type SortPreference } from "@/hooks/use-sort-preference";
import { type BookingFilters } from "@/routes/(authed)/_layout/bookings.route";

type GetBookingsSyncedSearchParamsArgs = {
  search: BookingFilters;
  storedPageSize: PageSize | null;
  storedSort: SortPreference | null;
  validSortFields: readonly BookingsSortField[];
};

/**
 * Determines if URL search params need to be synced with stored preferences.
 * Returns updated search params if sync is needed, or null if no sync required.
 *
 * @returns Updated search params if sync needed, null otherwise
 */
export function getBookingsSyncedSearchParams({
  search,
  storedPageSize,
  storedSort,
  validSortFields,
}: GetBookingsSyncedSearchParamsArgs): BookingFilters | null {
  const shouldSyncPageSize = !search.pageSize && storedPageSize;

  // Extract sortField to allow proper type narrowing
  const storedSortField = storedSort?.sortField;
  const validSortField = storedSortField && validSortFields.includes(storedSortField as BookingsSortField);
  const urlIsMissingSortParams = search.sortField === undefined && search.sortDesc === undefined;
  const shouldSyncSort = urlIsMissingSortParams && validSortField;

  if (!shouldSyncPageSize && !shouldSyncSort) {
    return null;
  }

  return {
    ...search,
    ...(shouldSyncPageSize && { pageSize: storedPageSize }),
    ...(shouldSyncSort && {
      sortField: storedSortField as BookingsSortField,
      sortDesc: storedSort?.sortDesc ?? false,
    }),
  };
}

export const DEFAULT_BOOKING_STATUSES = [
  "PLANNED",
  "BOOKED",
  "SHIPPED",
  "AWAITING_PAYMENT",
  "PAID",
  "DRAFT",
  "CANCELLED",
] as const satisfies NonNullable<BookingFilters["status"]>;

/**
 * The default statuses are a convenience for a first visit only. An explicit
 * empty `status` array means the user turned every status off on purpose, so it
 * must be distinguished from `status` being absent.
 */
export function shouldApplyDefaultStatuses(search: BookingFilters): boolean {
  const hasAnyFilter =
    Boolean(search.name) ||
    Boolean(search.lotNumber) ||
    Boolean(search.accountNumber) ||
    Boolean(search.dateOpenedAfter) ||
    Boolean(search.dateOpenedBefore) ||
    search.status !== undefined;

  return !hasAnyFilter;
}
